import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { ProTable } from "@ant-design/pro-components";
import { Button, Input, ConfigProvider } from "antd";
import api_address from "../../../constants/config";
import { ADMIN_COOKIE_NAME, LIMIT } from "../../../constants/admin";
import { GET_DATE_TIME_STRING_FROM_TIMESTAMP } from "../../../util/common";
import { JS_COOKIE } from "util/auth";
import { Container, AdminTableContainer } from "../styles";
import enUS from "antd/es/locale/en_US";
import { PRODUCTS } from "../../../constants/common";

interface PaymentType {
  id: string;
  transactionId: string;
  status: string;
  productName: string;
  userId: string;
  createdAt: string;
  paymentMethod: string;
}

interface SorterState {
  field: string;
  order: "ascend" | "descend" | undefined;
}

const Payment: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [paymentData, setPaymentData] = useState<PaymentType[]>([]);
  const [filteredData, setFilteredData] = useState<PaymentType[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [sorter, setSorter] = useState<SorterState | null>(null);
  const [filters, setFilters] = useState<{
    status?: string[];
    productName?: string[];
  }>({});
  const [searchText, setSearchText] = useState<string>("");
  const [pageSize, setPageSize] = useState<number>(LIMIT);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const getPaymentData = async (page: number = 1, pageSize: number = LIMIT) => {
    setLoading(true);
    try {
      const token = JS_COOKIE.get(ADMIN_COOKIE_NAME);
      const offset = (page - 1) * pageSize;
      const { data } = await axios.get(api_address + "api/admin/payment", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const sortedData = sortData(data.payment, sorter);
      setPaymentData(sortedData);
      setFilteredData(sortedData);
      setTotal(data.rowCount);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const productFilters = Object.keys(PRODUCTS).map((key) => ({
    text: PRODUCTS[key].replace(/([A-Z])/g, " $1").trim(),
    value: PRODUCTS[key],
  }));

  useEffect(() => {
    getPaymentData(currentPage, pageSize);
  }, [currentPage, pageSize]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [filters, sorter, paymentData, searchText]);

  const applyFiltersAndSort = () => {
    let result = [...paymentData];

    if (searchText) {
      result = result.filter((payment) =>
        Object.values(payment).some((value) =>
          String(value).toLowerCase().includes(searchText.toLowerCase())
        )
      );
    }

    if (filters.status && filters.status.length > 0) {
      result = result.filter((payment) =>
        filters.status!.includes(payment.status)
      );
    }

    if (filters.productName && filters.productName.length > 0) {
      result = result.filter((payment) =>
        filters.productName!.includes(payment.productName)
      );
    }

    result = sortData(result, sorter);

    setFilteredData(result);
    setTotal(result.length);
  };

  const sortData = (data: PaymentType[], currentSorter: SorterState | null) => {
    if (!currentSorter) return data;

    return [...data].sort((a, b) => {
      if (currentSorter.order === "ascend") {
        return a[currentSorter.field] > b[currentSorter.field] ? 1 : -1;
      } else if (currentSorter.order === "descend") {
        return a[currentSorter.field] < b[currentSorter.field] ? 1 : -1;
      }
      return 0;
    });
  };

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    const newSorter: SorterState = {
      field: sorter.field,
      order: sorter.order,
    };
    setSorter(newSorter);
    setFilters(filters);
    setPageSize(pagination.pageSize);
    setCurrentPage(pagination.current);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const columns = [
    {
      title: t("admin.payment.id"),
      dataIndex: "id",
      sorter: true,
    },
    {
      title: t("admin.payment.transactionId"),
      dataIndex: "transactionId",
      sorter: true,
    },
    {
      title: t("admin.payment.paymentMethod.title"),
      dataIndex: "paymentMethod",
      sorter: true,
    },
    {
      title: t("admin.payment.status.title"),
      dataIndex: "status",
      sorter: false,
      filters: [
        { text: "Success", value: "success" },
        { text: "Succeeded", value: "succeeded" },
        { text: "Finished", value: "TRADE_FINISHED" },
        { text: "Pending", value: "pending" },
        { text: "Failed", value: "failed" },
      ],
    },
    {
      title: t("admin.payment.product"),
      dataIndex: "productName",
      sorter: false,
      filters: productFilters,
    },
    {
      title: t("admin.user.id"),
      dataIndex: "userId",
      sorter: true,
      render: (text: string) => (
        <a onClick={() => history.push(`/admin/user/edit/${text}`)}>{text}</a>
      ),
    },
    {
      title: t("admin.payment.createdAt"),
      dataIndex: "createdAt",
      sorter: true,
      render: (text: string) => GET_DATE_TIME_STRING_FROM_TIMESTAMP(text),
    },
  ];

  return (
    <ConfigProvider locale={enUS}>
      <AdminTableContainer>
        <Input.Search
          placeholder={t("admin.payment.transactionId")}
          onChange={handleSearch}
          style={{ marginBottom: 16 }}
        />
        <ProTable<PaymentType>
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          rowKey="id"
          pagination={{
            showQuickJumper: true,
            total: total,
            pageSize: pageSize,
            current: currentPage,
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "25", "50", "100"],
          }}
          search={false}
          dateFormatter="string"
          headerTitle={t("admin.payment.title")}
          rowSelection={{
            onChange: (selectedRowKeys) => {
              setSelectedRowKeys(selectedRowKeys);
            },
          }}
          toolBarRender={() => [
            selectedRowKeys.length === 1 && (
              <Button
                key="edit"
                onClick={() =>
                  history.push(`/admin/payment/edit/${selectedRowKeys[0]}`)
                }
              >
                {t("admin.user.edit")}
              </Button>
            ),
          ]}
          onChange={handleTableChange}
          onRow={(record) => {
            return {
              onDoubleClick: () => {
                history.push(`/admin/payment/edit/${record.id}`);
              },
            };
          }}
        />
      </AdminTableContainer>
    </ConfigProvider>
  );
};

export default Payment;
